import { useMemo } from "react"
import { useNavigate } from "react-router-dom"
import MDButton from "components/MDButton"
import MDTypography from "components/MDTypography"
import { Page, Button } from "./ui"
import Icon from "@mui/material/Icon"
import StatusBadge from "./components/StatusBadge"
import ProcurementFilterBar from "./components/ProcurementFilterBar"
import ScopedDataTable from "./components/ScopedDataTable"
import useProcurementFilters from "./hooks/useProcurementFilters"
import { useGetSuppliersV2Query } from "shared/redux/endpoints/procurementV2"

const FILTER_DEFAULTS = {
  search: "",
  region: "",
  center: "",
  category: "",
  pendingApproval: "",
}

const SuppliersContainer = () => {
  const navigate = useNavigate()
  const { filters, setFilters, resetFilters } =
    useProcurementFilters(FILTER_DEFAULTS)
  const { data, isFetching } = useGetSuppliersV2Query(filters)

  const awaiting = filters.pendingApproval === "1"

  const columns = useMemo(
    () => [
      { Header: "Supplier", accessor: "name" },
      { Header: "Tax Code", accessor: "taxCode" },
      { Header: "Region", accessor: "region" },
      {
        Header: "KYC Status",
        accessor: "kycStatus",
        Cell: ({ value }) => <StatusBadge status={value} />,
      },
      {
        Header: "Performance",
        accessor: "performanceScore",
        Cell: ({ value }) => (
          <MDTypography variant="caption" fontWeight="medium">
            {value != null ? `${value}/100` : "—"}
          </MDTypography>
        ),
      },
      {
        Header: "Active",
        accessor: "active",
        Cell: ({ value }) => (
          <Icon color={value ? "success" : "disabled"} fontSize="small">
            {value ? "check_circle" : "cancel"}
          </Icon>
        ),
      },
      { Header: "Last Used", accessor: "lastUsed" },
      {
        Header: "",
        accessor: "id",
        Cell: ({ value }) => (
          <Button size="sm" variant="secondary" onClick={() => navigate(`/procurement/suppliers/${value}`)}>
            View
          </Button>
        ),
      },
    ],
    [navigate]
  )

  return (
    <Page
      className="pc-page--refined pc-page--list"
      breadcrumb={["Procurement", "Suppliers"]}
      title="Suppliers"
      subtitle="Manage supplier KYC, performance and activity across regions and centers."
    >
      <ProcurementFilterBar
        fields={["search", "region", "center", "category"]}
        values={filters}
        onChange={setFilters}
        onReset={resetFilters}
        extra={
          <MDButton
            variant={awaiting ? "gradient" : "outlined"}
            color="warning"
            onClick={() =>
              setFilters({ pendingApproval: awaiting ? "" : "1" })
            }
          >
            Awaiting KYC review
          </MDButton>
        }
      />

      <div className="ds-table-shell">
        <ScopedDataTable
          columns={columns}
          rows={data}
          isLoading={isFetching}
          emptyText="No suppliers found."
        />
      </div>
    </Page>
  )
}

export default SuppliersContainer
